import React, { useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import "@/App.css";
import { SITES } from "@/config/sites";
import { AuthProvider } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { SiteThemeProvider } from "@/contexts/SiteThemeContext";
import SiteHeader from "@/components/site/SiteHeader";
import SiteFooter from "@/components/site/SiteFooter";
import MarketplaceHome from "@/components/site/MarketplaceHome";
import { BANK_REFUSALS } from "@/config/bankRefusals";
import { LENDER_PAGES } from "@/config/lenderPages";
import { COMPARISON_PAGES } from "@/config/comparisonPages";
import { CITY_PAGES } from "@/config/cityPages";
import { VEHICLE_PAGES } from "@/config/vehiclePages";
import { CALCULATOR_PAGES, CALCULATOR_PAGES_EN } from "@/config/calculatorPages";
import CompareTray from "@/components/site/CompareTray";
import SocialProofStrip from "@/components/site/SocialProofStrip";
import { CAMPAIGN_SLUGS } from "@/config/campaignPages";
import CookieConsent from "@/components/site/CookieConsent";
import { ApplyCtaPanel, StickyMobileApplyCta } from "@/components/site/ApplyCta";

// Route-level code splitting keeps the public homepage bundle small. Heavy
// admin charts, calculators, funnel, and long-tail SEO templates load on demand.
const LandingPage = React.lazy(() => import("@/components/site/LandingPage"));
const ApplyPage = React.lazy(() => import("@/components/site/ApplyPage"));
const LegalPage = React.lazy(() => import("@/components/site/LegalPage"));
const AdminLogin = React.lazy(() => import("@/components/admin/AdminLogin"));
const AdminDashboard = React.lazy(() => import("@/components/admin/AdminDashboard"));
const AdminSeoPages = React.lazy(() => import("@/components/admin/AdminSeoPages"));
const AdminAnalytics = React.lazy(() => import("@/components/admin/AdminAnalytics"));
const AdminRoi = React.lazy(() => import("@/components/admin/AdminRoi"));
const SituationsHub = React.lazy(() => import("@/components/site/SituationsHub").then((m) => ({ default: m.SituationsHub })));
const SituationPage = React.lazy(() => import("@/components/site/SituationsHub").then((m) => ({ default: m.SituationPage })));
const SubprimeSimulator = React.lazy(() => import("@/components/site/SubprimeSimulator"));
const LendersDirectory = React.lazy(() => import("@/components/site/LendersDirectory"));
const GuidesHub = React.lazy(() => import("@/components/site/GuidesHub").then((m) => ({ default: m.GuidesHub })));
const GuideArticle = React.lazy(() => import("@/components/site/GuidesHub").then((m) => ({ default: m.GuideArticle })));
const CalculatorPage = React.lazy(() => import("@/components/site/CalculatorPage"));
const CalculatorsHub = React.lazy(() => import("@/components/site/CalculatorsHub"));
const ResourceCenter = React.lazy(() => import("@/components/site/ResourceCenter"));
const MyRebuildPlan = React.lazy(() => import("@/components/site/MyRebuildPlan"));
const CampaignLandingPage = React.lazy(() => import("@/components/site/CampaignLandingPage"));
const StaticOrDynamicRoute = React.lazy(() => import("@/components/site/StaticOrDynamicRoute"));
import { DEFAULT_SITE, resolveSiteFromHostname } from "@/lib/siteResolver";
import { initAnalytics, trackPageView } from "@/lib/analytics";
import { captureAttribution } from "@/lib/attribution";

// Scroll the window back to the top whenever the route pathname changes,
// otherwise React Router preserves the previous scroll position and each
// sub-page can feel like the same page.
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" in window ? "instant" : "auto" });
  }, [pathname]);
  return null;
}

// Fires a page_view event on every client-side navigation. Sends to GA4
// (if consented + configured), Meta Pixel (if consented + configured), and
// always to the first-party /api/events endpoint.
function PageViewTracker() {
  const { pathname, search } = useLocation();
  useEffect(() => {
    // Capture attribution BEFORE page_view so utm/gclid/fbclid land in
    // sessionStorage in time for downstream lead POSTs to pick them up.
    captureAttribution();
    // Defer to next tick so document.title (set by page-level useSeo hooks)
    // has landed before we snapshot it.
    const t = setTimeout(() => trackPageView(pathname + search, document.title), 50);
    return () => clearTimeout(t);
  }, [pathname, search]);
  return null;
}
function RouteFallback() {
  return (
    <div className="flex min-h-[280px] items-center justify-center bg-slate-50" data-testid="route-loading">
      <div className="text-sm font-semibold text-slate-500">Chargement…</div>
    </div>
  );
}


function SiteWrapper({ children }) {
  const { siteKey } = useParams();
  const { pathname } = useLocation();
  // Rétablirmoncredit.com is the only public site — redirect any legacy siteKey
  // (bad-credit, rebuild, etc.) to /approval.
  if (siteKey !== "approval") return <Navigate to={`/${DEFAULT_SITE}`} replace />;
  if (!SITES[siteKey]) return <Navigate to={`/${DEFAULT_SITE}`} replace />;
  const isApply = pathname.endsWith("/apply");
  return (
    <SiteThemeProvider siteKey={siteKey}>
      {!isApply && <SiteHeader />}
      <main>{children}</main>
      {!isApply && (
        <ApplyCtaPanel
          placement="global-bottom"
          compact
          className="mx-auto mt-12 max-w-6xl px-4 sm:px-6 lg:px-8"
        />
      )}
      <SiteFooter />
      <StickyMobileApplyCta />
      <div className="h-20 sm:hidden" aria-hidden="true" />
    </SiteThemeProvider>
  );
}
function GlobalOverlays() {
  const { pathname } = useLocation();
  const isApply = pathname.endsWith("/apply");
  return (
    <>
      <CookieConsent />
      {!isApply && <CompareTray />}
      {!isApply && <SocialProofStrip />}
    </>
  );
}


function SiteRoutes() {
  const { siteKey } = useParams();
  const site = SITES[siteKey];
  if (!site) return <Navigate to={`/${DEFAULT_SITE}`} replace />;

  // approval (Rétablirmoncredit.com) uses the marketplace layout + new pages;
  // other sites keep the dealership LandingPage layout.
  const isMarketplace = siteKey === "approval";

  return (
    <React.Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route index element={isMarketplace ? <MarketplaceHome /> : <LandingPage pageKey="home" />} />
      {isMarketplace && (
        <>
          <Route path="situations" element={<SituationsHub />} />
          <Route path="situations/:slug" element={<SituationPage />} />
          <Route path="simulator" element={<SubprimeSimulator />} />
          <Route path="lenders" element={<LendersDirectory />} />
          <Route path="preteurs" element={<LendersDirectory />} />
          <Route path="guides" element={<GuidesHub />} />
          <Route path="guides/:slug" element={<GuideArticle />} />
          <Route path="refuse-par/:bank" element={<BankRefusalRoute />} />
          <Route path="preteur/:slug" element={<LenderPageRoute />} />
          <Route path="comparaison/:slug" element={<ComparisonPageRoute />} />
          <Route path="ville/:slug" element={<CityPageRoute />} />
          <Route path="vehicule/:slug" element={<VehiclePageRoute />} />
          <Route path="outils" element={<CalculatorsHub />} />
          <Route path="tools" element={<CalculatorsHub />} />
          <Route path="ressources" element={<ResourceCenter />} />
          <Route path="resources" element={<ResourceCenter />} />
          <Route path="mon-plan" element={<MyRebuildPlan />} />
          <Route path="my-plan" element={<MyRebuildPlan />} />
          {CAMPAIGN_SLUGS.map((slug) => (
            <Route key={slug} path={slug} element={<CampaignLandingPage slug={slug} />} />
          ))}
          <Route path="outils/:slug" element={<CalculatorPageRoute langUrl="fr" />} />
          <Route path="tools/:slug" element={<CalculatorPageRoute langUrl="en" />} />
        </>
      )}
      {Object.entries(site.pages)
        .filter(([k]) => !["home", "apply", "contact", "situations", "simulator", "lenders", "guides", "outils", "tools", "ressources", "resources", "monPlan", "mon-plan", "my-plan"].includes(k))
        .map(([k, p]) => (
          <Route key={k} path={p.path} element={<LandingPage pageKey={k} />} />
        ))}
      <Route path="apply" element={<ApplyPage />} />
      <Route path="contact" element={<LegalPage variant="contact" />} />
      <Route path="privacy" element={<LegalPage variant="privacy" />} />
      <Route path="terms" element={<LegalPage variant="terms" />} />
        <Route path="*" element={<Navigate to="" replace />} />
      </Routes>
    </React.Suspense>
  );
}

function BankRefusalRoute() {
  const { bank } = useParams();
  return (
    <StaticOrDynamicRoute
      type="bank"
      slug={bank}
      staticData={BANK_REFUSALS[bank]}
      canonicalPathPrefix="/approval/refuse-par"
      crossLinkType="bank"
      testIdPrefix="bank-refusal"
    />
  );
}

function LenderPageRoute() {
  const { slug } = useParams();
  return (
    <StaticOrDynamicRoute
      type="lender"
      slug={slug}
      staticData={LENDER_PAGES[slug]}
      canonicalPathPrefix="/approval/preteur"
      crossLinkType="lender"
      testIdPrefix="lender"
    />
  );
}

function ComparisonPageRoute() {
  const { slug } = useParams();
  return (
    <StaticOrDynamicRoute
      type="comparison"
      slug={slug}
      staticData={COMPARISON_PAGES[slug]}
      canonicalPathPrefix="/approval/comparaison"
      crossLinkType="comparison"
      testIdPrefix="comparison"
    />
  );
}

function CityPageRoute() {
  const { slug } = useParams();
  return (
    <StaticOrDynamicRoute
      type="city"
      slug={slug}
      staticData={CITY_PAGES[slug]}
      canonicalPathPrefix="/approval/ville"
      crossLinkType="city"
      testIdPrefix="city"
    />
  );
}

function VehiclePageRoute() {
  const { slug } = useParams();
  return (
    <StaticOrDynamicRoute
      type="vehicle"
      slug={slug}
      staticData={VEHICLE_PAGES[slug]}
      canonicalPathPrefix="/approval/vehicule"
      crossLinkType="vehicle"
      testIdPrefix="vehicle"
    />
  );
}

function CalculatorPageRoute({ langUrl }) {
  const { slug } = useParams();
  const data = langUrl === "en" ? CALCULATOR_PAGES_EN[slug] : CALCULATOR_PAGES[slug];
  if (!data) return <Navigate to={`/${DEFAULT_SITE}/outils`} replace />;
  return <CalculatorPage calc={data} langUrl={langUrl} />;
}


function RootRedirect() {
  // Route the root URL to the correct site based on the current hostname.
  // - Real production domain (e.g. badcreditautoquebec.ca) → that site as root.
  // - Emergent preview URL → best-effort slug match, fallback to default site.
  const target = resolveSiteFromHostname() || DEFAULT_SITE;
  return <Navigate to={`/${target}`} replace />;
}

/** Preserves query params on redirect from paid-campaign short URL to
 *  the canonical /approval/{slug} route. Attribution stays intact. */
function CampaignRedirect({ slug }) {
  const { search } = useLocation();
  return <Navigate to={`/approval/${slug}${search}`} replace />;
}

function App() {
  useEffect(() => {
    initAnalytics();
  }, []);
  return (
    <BrowserRouter>
      <AuthProvider>
        <LanguageProvider>
          <ScrollToTop />
          <PageViewTracker />
          <React.Suspense fallback={<RouteFallback />}>
            <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/seo" element={<AdminSeoPages />} />
            <Route path="/admin/analytics" element={<AdminAnalytics />} />
            <Route path="/admin/roi" element={<AdminRoi />} />
            {/* Paid-campaign short URLs (root-level) redirect to /approval/{slug}
                so we keep the SEO canonical inside the marketplace tree and
                preserve utm/gclid/fbclid query params on the redirect. */}
            {CAMPAIGN_SLUGS.map((slug) => (
              <Route
                key={`root-${slug}`}
                path={`/${slug}`}
                element={<CampaignRedirect slug={slug} />}
              />
            ))}
            <Route
              path="/:siteKey/*"
              element={
                <SiteWrapper>
                  <SiteRoutes />
                </SiteWrapper>
              }
            />
            <Route path="*" element={<RootRedirect />} />
            </Routes>
          </React.Suspense>
          <GlobalOverlays />
        </LanguageProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
